import { useState } from 'react';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { ChevronDown, FolderTree, type LucideIcon } from 'lucide-react';
import { cn } from '@/lib/utils';

export function PickerSelect({
  tone = 'primary', label, placeholder, options, value, onChange, disabled, icon: Icon = FolderTree,
}: {
  tone?: 'primary' | 'accent';
  label?: string;
  placeholder: string;
  options: { id: string; label: string }[];
  value: string | null;
  onChange: (v: string) => void;
  disabled?: boolean;
  icon?: LucideIcon;
}) {
  const [open, setOpen] = useState(false);
  const selected = options.find(o => o.id === value);
  const toneBg = tone === 'primary' ? 'bg-primary/10 text-primary' : 'bg-accent/10 text-accent';
  return (
    <Collapsible open={open && !disabled} onOpenChange={(v) => !disabled && setOpen(v)}>
      <CollapsibleTrigger asChild>
        <button
          type="button"
          disabled={disabled}
          className={cn(
            'w-full flex items-center justify-between gap-3 h-14 px-4 rounded-lg border border-border bg-background/80 hover:border-primary/50 transition-all text-start disabled:opacity-60 disabled:cursor-not-allowed',
            open && !disabled && 'border-primary/60 bg-background'
          )}
        >
          <span className="flex items-center gap-3 min-w-0 flex-1">
            <span className={cn('rounded-md p-1.5 shrink-0', toneBg)}>
              <Icon className="w-4 h-4" />
            </span>
            <span className="min-w-0 flex-1">
              {label && <span className="block text-[11px] uppercase tracking-wider text-muted-foreground leading-tight">{label}</span>}
              <span className="block text-sm font-semibold leading-tight truncate">{selected ? selected.label : placeholder}</span>
            </span>
          </span>
          <ChevronDown className={cn('w-4 h-4 text-muted-foreground transition-transform shrink-0', open && !disabled && 'rotate-180')} />
        </button>
      </CollapsibleTrigger>
      <CollapsibleContent className="overflow-hidden data-[state=open]:animate-accordion-down data-[state=closed]:animate-accordion-up">
        <div className="mt-2 rounded-lg border border-border bg-background/40 p-1.5 max-h-60 overflow-y-auto space-y-1">
          {options.length === 0 && (
            <div className="px-3 py-2 text-xs text-muted-foreground">{placeholder}</div>
          )}
          {options.map(o => {
            const isSel = o.id === value;
            return (
              <button
                key={o.id}
                type="button"
                onClick={() => { onChange(o.id); setOpen(false); }}
                className={cn(
                  'w-full flex items-center justify-between gap-2 px-3 py-2 rounded-md text-start text-sm transition-colors',
                  isSel ? 'bg-primary/15 text-foreground font-semibold' : 'hover:bg-muted/60'
                )}
              >
                <span className="truncate">{o.label}</span>
                {isSel && <span className="text-primary text-xs">✓</span>}
              </button>
            );
          })}
        </div>
      </CollapsibleContent>
    </Collapsible>
  );
}
